/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

// --- Helper Functions ---

/**
 * Checks if a value is empty or whitespace-only
 */
const isEmptyOrWhitespace = (val: unknown): boolean => {
  if (typeof val !== 'string') return false;
  return val.trim().length === 0;
};

/**
 * Safely converts a value to a string representation
 */
const safeStringify = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'boolean') return String(val);
  if (typeof val === 'number') return String(val);
  if (val instanceof Date) return val.toISOString();
  return String(val);
};

// --- Transformation Functions ---

/**
 * Transforms individual education fields into an education array
 * Converts latest_degree/latest_degree_college/latest_degree_year and
 * previous_degree/previous_degree_college/previous_degree_year into
 * a single education array format expected by the server
 */
export const transformEducationFields = (data: Record<string, unknown>): Record<string, unknown> => {
  const transformedData = { ...data };
  const educationArray: Array<{ degree: string; college: string; year: string }> = [];

  // Build education array from individual fields
  if (data.latest_degree && data.latest_degree_college && data.latest_degree_year) {
    educationArray.push({
      degree: String(data.latest_degree),
      college: String(data.latest_degree_college),
      year: String(data.latest_degree_year),
    });
  }

  if (data.previous_degree && data.previous_degree_college && data.previous_degree_year) {
    educationArray.push({
      degree: String(data.previous_degree),
      college: String(data.previous_degree_college),
      year: String(data.previous_degree_year),
    });
  }

  // Replace individual fields with education array if we have any education data
  if (educationArray.length > 0) {
    transformedData.education = educationArray;
    delete transformedData.latest_degree;
    delete transformedData.latest_degree_college;
    delete transformedData.latest_degree_year;
    delete transformedData.previous_degree;
    delete transformedData.previous_degree_college;
    delete transformedData.previous_degree_year;
  }

  return transformedData;
};

// --- FormData Builder ---

/**
 * Builds FormData from profile form data
 * Handles all field types: strings, numbers, booleans, files, simple arrays, and complex arrays
 */
export const buildProfileFormData = (data: Record<string, unknown>): FormData => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    // Skip null and undefined values
    if (value === null || value === undefined) {
      return;
    }

    // Handle file uploads (resume_cv)
    if (key === 'resume_cv') {
      if (!(value instanceof File)) {
        // If resume_cv is provided but not a File, skip it
        return;
      }
      // File validation already done in validateProfileForm
      // Append file with explicit filename
      const filename = value.name || 'resume.pdf';
      formData.append(key, value, filename);
      return;
    }

    // Handle array values
    if (Array.isArray(value)) {
      // Special handling for education array (array of objects)
      if (key === 'education') {
        value.forEach((edu, index) => {
          if (edu && typeof edu === 'object') {
            formData.append(`education[${index}][degree]`, String(edu.degree || ''));
            formData.append(`education[${index}][college]`, String(edu.college || ''));
            formData.append(`education[${index}][year]`, String(edu.year || ''));
          }
        });
        return;
      }

      // Handle simple string arrays (like languages_written_proficiency)
      const validItems = value.filter((item) => item !== null && item !== undefined && !isEmptyOrWhitespace(item));

      // Only append if there are valid items
      if (validItems.length > 0) {
        validItems.forEach((item) => {
          const stringValue = safeStringify(item);
          if (!isEmptyOrWhitespace(stringValue)) {
            formData.append(`${key}[]`, stringValue);
          }
        });
      }
      return;
    }

    // Handle non-file, non-array values
    const stringValue = safeStringify(value);

    // Skip empty strings and whitespace-only values
    if (!isEmptyOrWhitespace(stringValue)) {
      formData.append(key, stringValue);
    }
  });

  return formData;
};
